/**
 * imports
 */
import { createApp } from 'vue';
import { createStore } from 'vuex';
import VueLazyload from 'vue-lazyload';
import './css/main.css';
import { currencyJson } from './currency';
import lenisScroll from './vue/directives/lenisScroll';
// import { shopConfig } from './config/shopConfig';
/**
 * vuex modules auto-load
 */
const vuexModules = require.context('./vue/store/', true, /\.js$/);
const modules = {};

vuexModules.keys().forEach((key) => {
  const name = key.replace(/\.(\/|js)/g, '').replace(/\s/g, '-');
  modules[name] = vuexModules(key).default;
});

const store = createStore({
  strict: process.env.NODE_ENV !== 'production',
  modules,
});

/**
 * 🧩 Create Vue app factory
 */
const createVueApp = () => {
  const app = createApp({});

  // 👇 Add Debug Error Handler — helps find "Unexpected number" + component
  app.config.errorHandler = (err, vm, info) => {
    console.group('%c🔥 Vue Error Caught', 'color:red;font-weight:bold;');
    console.error('➡️ Message:', err.message);
    console.log('📦 Component:', vm?.$options?.name || '(anonymous)');
    console.log('ℹ️ Info:', info);
    console.log('💻 Stack:', err.stack);
    console.groupEnd();
  };

  // Directives
  app.directive('lenisScroll', lenisScroll);

  // 💲 Currency Symbol
  app.config.globalProperties.$currencySymbol =
    currencyJson[window?.Shopify?.currency?.active]?.symbol_native;
  // app.config.globalProperties.$currencySymbol = currencyJson?.[window?.Shopify?.currency?.active]?.symbol_native || '$';

  // app.config.globalProperties.$currencyCode = shopConfig.currency;
  // app.config.globalProperties.$shopLocale = shopConfig.locale;
  app.config.globalProperties.$log = console.log;

  /**
   * Auto-register Vue Components
   */
  const vueComponents = require.context('./vue/components/', true, /\.(vue|js)$/);
  vueComponents.keys().forEach((key) => {
    const component = vueComponents(key).default;
    const name = component.name
      ? component.name
      : key
          .replace(/\.(\/|vue|js)/g, '')
          .replace(/(\/|-|_|\s)\w/g, (m) => m.slice(1).toUpperCase());
    try {
      app.component(name, component);
    } catch (error) {
      console.error(`❌ Error registering component ${name}:`, error);
    }
  });

  /**
   * Auto-register Global Mixins
   */
  const mixins = require.context('./vue/mixins/', true, /.*global.*\.js$/);
  mixins.keys().forEach((key) => {
    try {
      app.mixin(mixins(key).default);
    } catch (e) {
      console.warn(`Mixin load failed: ${key}`, e);
    }
  });

  /**
   * Auto-register Global Directives
   */
  const directives = require.context('./vue/directives/', true, /.*global.*\.js$/);
  directives.keys().forEach((key) => {
    const directive = directives(key).default;
    app.directive(directive.name, directive.directive);
  });

  /**
   * 🧩 Avoid duplicate plugin apply
   */
  if (!app._pluginsRegistered) {
    app.use(VueLazyload);
    app.use(store);
    app._pluginsRegistered = true;
  }

  return app;
};

const mountApp = (el) => {
  try {
    // Skip if already mounted
    if (el.__vue_app__) {
      return;
    }
    const app = createVueApp();
    app.mount(el);
    // console.log(`✅ Mounted Vue on`, el);
  } catch (error) {
    console.error('💥 Error mounting Vue app:', error);
  }
};

// Mount Vue on #app (original working approach)
// const appElement = document.querySelector('#app');
// if (appElement) {
//   mountApp(appElement);
// } else {
//   document.querySelectorAll('[vue]').forEach(mountApp);
// }

/**
 * 🧩 Check if element contains Vue components (render-* tags)
 */
const hasVueComponents = (el) => {
  // Method 1: Check for explicit [vue] attribute
  if (el.hasAttribute('vue')) return true;

  // Method 2: Find any element whose tag starts with RENDER-
  const allElements = el.getElementsByTagName('*');
  for (let i = 0; i < allElements.length; i++) {
    if (allElements[i].tagName.startsWith('RENDER-')) {
      return true;
    }
  }
  return false;
};

/**
 * 🧩 Vue Mounting Strategy
 * ================================
 * - Mount Vue ONLY on Shopify sections that contain render-* components
 * - Pure Liquid/HTML sections remain completely untouched (CSS works!)
 * - Uses robust tagName detection that works in production
 */
const initVueApp = () => {
  // Mount on explicit [vue] attribute elements
  document.querySelectorAll('[vue]:not([data-vue-mounted])').forEach((el) => {
    el.setAttribute('data-vue-mounted', 'true');
    mountApp(el);
  });

  // Auto-detect Shopify sections with Vue components
  document
    .querySelectorAll('[id^="shopify-section-"]:not([data-vue-mounted])')
    .forEach((section) => {
      if (hasVueComponents(section)) {
        section.setAttribute('data-vue-mounted', 'true');
        mountApp(section);
      }
    });
};

// Initialize Vue when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVueApp);
} else {
  initVueApp();
}

/**
 * 🧩 Shopify Theme Editor Support
 */
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (hasVueComponents(section) && !section.hasAttribute('data-vue-mounted')) {
      section.setAttribute('data-vue-mounted', 'true');
      mountApp(section);
    }
  });
}
